import { BotId, KnowledgeBaseQuery, NlpModelId as ModelId, QueryListFilter } from '@feedyou/schema'
import {
  keysToCamelCase,
  IndexRunResult,
  KBDocument,
  KnowledgeBaseDocumentChunk,
  KnowledgeBasePath,
  KnowledgeBaseSettings
} from '@feedyou/utils'

import { TableContinuationToken } from './types'
import { serverApi, ServerApiTag } from '../../store/serverApi'
import { bindSideEffects } from '../../lib/rtkq/bindSideEffects'
import { toastNotifyPathDeleteSuccess, toastNotifyTooLargeFilesError } from './toasts'

type QueryResult = {
  queries: KnowledgeBaseQuery[]
  continuationToken?: TableContinuationToken
}

export const knowledgeBaseApi = serverApi.injectEndpoints({
  endpoints: builder => ({
    getKnowledgeBaseQuery: builder.query<
      QueryResult,
      {
        botId: BotId
        modelId: ModelId
        queryFilter: QueryListFilter
        continuationToken?: TableContinuationToken
      }
    >({
      query: ({ botId, modelId, queryFilter, continuationToken }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/query`,
        params: { ...queryFilter, continuationToken }
      }),
      transformResponse: (response: QueryResult) => {
        // note: for purpose of backward compatibility
        return {
          ...response,
          queries: response.queries.map(keysToCamelCase).map(query => ({
            ...query,
            day: query.partitionKey
          })) as KnowledgeBaseQuery[]
        }
      },
      providesTags: [ServerApiTag.KNOWLEDGE_BASE_QUERY]
    }),
    getKnowledgeBaseDocuments: builder.query<
      KBDocument<'raw'>[],
      { botId: BotId; modelId: ModelId }
    >({
      query: ({ botId, modelId }) => `/bots/${botId}/nlp/model/${modelId}/kb/documents`,
      providesTags: (_, __, arg) => [ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(arg.modelId)]
    }),
    getKnowledgeBasePaths: builder.query<KnowledgeBasePath[], { botId: BotId; modelId: ModelId }>({
      query: ({ botId, modelId }) => `/bots/${botId}/nlp/model/${modelId}/kb/paths`,
      providesTags: [ServerApiTag.KNOWLEDGE_BASE_PATHS]
    }),
    addKnowledgeBaseDocument: builder.mutation<
      any,
      {
        botId: string
        modelId: string
        files: { path: string; filename: string; data: string }[]
      }
    >({
      query: ({ botId, modelId, files }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/documents`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: { files: files }
      }),
      transformErrorResponse: (response, _, arg) => {
        response?.status === 413 && toastNotifyTooLargeFilesError(arg.files.length)
        return response
      },
      invalidatesTags: (_, __, arg) => [ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(arg.modelId)]
    }),
    putKnowledgeBaseDocument: builder.mutation<
      any,
      {
        botId: string
        modelId: string
        files: { path: string; filename: string; data: string; documentId: string }[]
      }
    >({
      query: ({ botId, modelId, files }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/documents`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { files: files }
      }),
      invalidatesTags: (_, __, arg) => [
        ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(arg.modelId),
        ServerApiTag.KNOWLEDGE_BASE_DOCUMENT_CONTENT
      ]
    }),
    putKnowledgeBaseDocumentSettings: builder.mutation<
      any,
      { botId: string; modelId: string; documentId: string; settings: string }
    >({
      query: ({ botId, modelId, documentId, settings }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/documents/${documentId}/settings`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { settings }
      }),
      invalidatesTags: (_, __, arg) => [ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(arg.modelId)]
    }),
    putKnowledgeBasePathSettings: builder.mutation<
      any,
      { botId: string; modelId: string; path: string; settings: KnowledgeBaseSettings }
    >({
      query: ({ botId, modelId, path, settings }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/paths/${path.replaceAll('/', '|')}/settings`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: { settings }
      }),
      invalidatesTags: (_, __, arg) => [
        ServerApiTag.KNOWLEDGE_BASE_PATHS,
        ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(arg.modelId)
      ]
    }),
    deleteKnowledgeBaseDocument: builder.mutation<
      any,
      { botId: string; modelId: string; documentPath: string }
    >({
      query: ({ botId, modelId, documentPath }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/documents`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: { documentPath }
      }),
      invalidatesTags: (_, __, arg) => [ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(arg.modelId)]
    }),
    deleteKnowledgeBasePath: builder.mutation<
      any,
      { botId: string; modelId: string; path: string }
    >({
      query: ({ botId, modelId, path }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/paths`,
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: { path }
      }),
      onQueryStarted: bindSideEffects({
        success: [params => toastNotifyPathDeleteSuccess(params.path)]
      }),
      invalidatesTags: (_, __, arg) => [ServerApiTag.KNOWLEDGE_BASE_DOCUMENTS(arg.modelId)]
    }),
    getKnowledgeBaseDocumentContent: builder.query<
      string,
      { botId: BotId; modelId: ModelId; documentName: string; type: 'markdown' | 'source' | 'html' }
    >({
      query: ({ botId, modelId, documentName, type }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/documents/${documentName}/preview/${type}`,
        responseHandler: async response => {
          const data = await response.text()
          return data
        }
      }),
      providesTags: [ServerApiTag.KNOWLEDGE_BASE_DOCUMENT_CONTENT]
    }),
    downloadDocument: builder.query<
      { fileName: string; blob: Blob },
      {
        botId: string
        modelId: string
        documentId: string
        format: 'source' | 'markdown'
      }
    >({
      query: ({ botId, modelId, documentId, format }) => ({
        url: `/bots/${botId}/nlp/model/${modelId}/kb/documents/${encodeURIComponent(
          documentId
        )}/download/${format}`,
        responseHandler: async response => response.blob()
      }),
      transformResponse: (response: any, meta: any) => ({
        blob: response,
        fileName: meta.response?.headers.get('x-file-name')
      }),
      keepUnusedDataFor: 0 // Don't cache blobs to avoid memory issues
    }),
    getKnowledgeBaseDocumentChunks: builder.query<
      KnowledgeBaseDocumentChunk[],
      { botId: BotId; modelId: ModelId; documentId: string }
    >({
      query: ({ botId, modelId, documentId }) =>
        `/bots/${botId}/nlp/model/${modelId}/kb/documents/${documentId}/chunks`,
      providesTags: [ServerApiTag.KNOWLEDGE_BASE_DOCUMENT_CHUNKS]
    }),
    indexNewDocuments: builder.mutation<void, { botId: BotId; modelId: ModelId }>({
      query: ({ botId, modelId }) => {
        return {
          method: 'POST',
          url: `/bots/${botId}/nlp/model/${modelId}/kb/indexNewStart`
        }
      },
      // No invalidation here - invalidate when index run finishes (reported via socket server)
      invalidatesTags: (_, __, arg) => []
    }),
    reindexAllDocuments: builder.mutation<void, { botId: BotId; modelId: ModelId }>({
      query: ({ botId, modelId }) => {
        return {
          method: 'POST',
          url: `/bots/${botId}/nlp/model/${modelId}/kb/reindexStart`
        }
      },
      // No invalidation here - invalidate when index run finishes (reported via socket server)
      invalidatesTags: (_, __, arg) => []
    }),
    getHistoricIndexRuns: builder.query<
      { runs: IndexRunResult[] },
      { botId: BotId; modelId: ModelId; count: number }
    >({
      query: ({ botId, modelId }) => {
        return {
          method: 'GET',
          url: `/bots/${botId}/nlp/model/${modelId}/kb/index-runs`,
          params: { count: 1 }
        }
      },
      providesTags: (_, __, arg) => [ServerApiTag.KNOWLEDGE_BASE_PAST_INDEX_RUNS(arg.modelId)]
    })
  })
})
